import noop from 'lodash/noop';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchState } from '@hotelplan/fdr.lib.search.with-state';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useTrackFiltersExceptions } from '@hotelplan/libs.tracking';
import { fdrSrlFormToCriteriaInput } from 'fdr/components/candidate/fdr-search/mappers/fdr-srl-form-to-criteria-input.mapper';
import {
  COMPARE_ITEMS_COUNT_DESKTOP,
  COMPARE_ITEMS_COUNT_MOBILE,
  COMPARE_ITEMS_MAX_COUNT,
} from 'fdr/components/local/fdr-wishlist/fdr-wishlist.constants';
import { IWishlistState } from 'fdr/components/local/fdr-wishlist/fdr-wishlist.types';
import { FdrWishlistWithModifiedOffersItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist-with-modified-offers.generated';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import { useFdrGetActiveWishlistQuery } from 'fdr/schemas/query/wishlist/fdr-get-active-wishlist.generated';
import { useFdrWishlistWithModifiedOffersQuery } from 'fdr/schemas/query/wishlist/fdr-wishlist-with-modified-offers.generated';
import useActiveWishlistId from './fdr-use-active-wishlist-id';

export type TWishlistSelectedItems = (
  | (Omit<FdrWishlistWithModifiedOffersItemFragment, 'trackedOffer'> & {
      trackedOffer: Omit<
        FdrWishlistWithModifiedOffersItemFragment['trackedOffer'],
        'modified'
      > & {
        modified?: FdrWishlistWithModifiedOffersItemFragment['trackedOffer']['modified'];
      };
    })
  | null
)[];

export interface IWishlistSelectedItemsContext {
  itemsLoading: boolean;
  items?: FdrWishlistItemFragment[] | null;
  selectedItems: TWishlistSelectedItems;
  updateItem: (newItem: FdrWishlistItemFragment | null, index?: number) => void;
  saveAll: (newItems: TWishlistSelectedItems) => void;
}

const FdrWishlistSelectedItemsContext =
  createContext<IWishlistSelectedItemsContext>({
    items: [],
    selectedItems: [],
    itemsLoading: false,
    updateItem: noop,
    saveAll: noop,
  });

export const WishlistSelectedItemsProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const id = useActiveWishlistId();
  const { mobile } = useDeviceType();

  const { query } = useRouter();

  const hasSearchQueryParam = Boolean('s' in query && query['s']);

  const [activeWishlistId, setActiveWishlistId] = useState<string | null>(id);

  const [items, setItems] = useState<FdrWishlistItemFragment[]>([]);
  const [selectedItems, setSelectedItems] = useState<
    (FdrWishlistItemFragment | null)[]
  >(Array.from({ length: COMPARE_ITEMS_MAX_COUNT }).map(() => null));

  // on sharing page `state` might be NULL due to missing search state context
  const { state } = useSearchState<IWishlistState>();

  const handleActiveWishlistItems = useCallback(
    (
      activeWishlistItems:
        | FdrWishlistItemFragment[]
        | FdrWishlistWithModifiedOffersItemFragment[]
    ) => {
      // NOTE: If the item is not selected, then just select first COMPARE_ITEMS_COUNT_XXX from the wishlist.
      const firstSelectedItems = activeWishlistItems.slice(
        0,
        mobile ? COMPARE_ITEMS_COUNT_MOBILE : COMPARE_ITEMS_COUNT_DESKTOP
      );

      setSelectedItems(
        selectedItems.map((item, itemIndex) => {
          if (!item) return firstSelectedItems[itemIndex];

          const updatedItem = activeWishlistItems.find(
            it => it.product.id === item.product.id
          );

          return updatedItem
            ? { ...updatedItem }
            : firstSelectedItems[itemIndex];
        }) as TWishlistSelectedItems
      );

      setItems(activeWishlistItems);
    },
    [mobile, selectedItems]
  );

  const {
    loading: itemsLoading,
    data,
    error,
  } = useFdrGetActiveWishlistQuery({
    ssr: false,
    notifyOnNetworkStatusChange: true,
    skip: hasSearchQueryParam,
  });

  const { loading: modifiedOffersLoading } =
    useFdrWishlistWithModifiedOffersQuery({
      variables: {
        wishlistModifiedOfferCriteria: fdrSrlFormToCriteriaInput(
          state?.searchControl,
          state?.filters
        ),
      },
      ssr: false,
      notifyOnNetworkStatusChange: true,
      skip: !hasSearchQueryParam,
      onCompleted: activeWishlist => {
        handleActiveWishlistItems(
          activeWishlist.fdrActiveWishlist.wishlist?.wishlistItems || []
        );
      },
    });

  useEffect(() => {
    handleActiveWishlistItems(
      data?.fdrActiveWishlist.wishlist?.wishlistItems || []
    );
  }, [data?.fdrActiveWishlist.wishlist]);

  const availableWishlistItemsCount =
    data?.fdrActiveWishlist.wishlist?.wishlistItems?.filter(
      item => item.trackedOffer.latest
    )?.length || 0;

  useTrackFiltersExceptions(availableWishlistItemsCount, itemsLoading, !!error);

  useEffect(() => {
    if (itemsLoading || modifiedOffersLoading || !id || id === activeWishlistId)
      return;

    setActiveWishlistId(id);
  }, [id, itemsLoading, modifiedOffersLoading]);

  const updateItem = (
    newItem: FdrWishlistItemFragment | null,
    index?: number
  ): void => {
    setSelectedItems(
      selectedItems.map((item, itemIndex) => {
        if (typeof index !== 'undefined') {
          return itemIndex === index ? newItem : item;
        }

        return item?.product.id === newItem?.product.id && newItem
          ? newItem
          : item;
      })
    );
    setItems(
      items.map(item =>
        item?.product.id === newItem?.product.id && newItem ? newItem : item
      )
    );
  };

  const saveAll = (newItems: (FdrWishlistItemFragment | null)[]): void => {
    setSelectedItems(newItems);
  };

  return (
    <FdrWishlistSelectedItemsContext.Provider
      value={{
        items,
        selectedItems,
        itemsLoading: itemsLoading || modifiedOffersLoading,
        updateItem,
        saveAll,
      }}
    >
      {children}
    </FdrWishlistSelectedItemsContext.Provider>
  );
};

export function useWishlistSelectedItemsContext() {
  return useContext(FdrWishlistSelectedItemsContext);
}
